// Mapbox Integration
mapboxgl.accessToken = 'pk.eyJ1Ijoic3lzYWRtaW4tbmNvbC1jb20iLCJhIjoiY21od2Zkdnk3MDY4NzJrb2UwY3R5cWEyOSJ9.r619fjt-RaxCTX18DkN2PQ';

class StoreLocations extends HTMLElement {
    constructor() {
        super();
        this.initial = true;
        this.dataset.loading = true;

        // Get base URL.
        this.basePath = this.dataset.basePath || "/pages/locations";
        this.documentTitle = document.title;

        // Get search form.
        this.searching = false;
        this.formSearch = this.querySelector("form[data-search]");
        this.searchInput = this.formSearch.querySelector('[name="search_locations"]');
        this.buttonSearch = this.formSearch.querySelector('[type="submit"]');
        this.feedback = this.formSearch.querySelector('[name="_feedback"]');

        // Get filters.
        this.filters = {
            quarks: this.formSearch.querySelector('[name="checkbox_quarks"]'),
            urbanTrail: this.formSearch.querySelector('[name="checkbox_urbantrail"]')
        };

        // Get my location button.
        this.buttonMyLocation = this.querySelector("button[data-my-location]");
        this.mapPosition = {};

        // Get map container.
        this.mapContainer = this.querySelector("[data-map]");
        this.mapStaticContainer = this.querySelector("[data-map-static]");

        // Setup Canada bounds.
        this.latLngBounds = {
            north: 70,
            south: 30,
            east: -53,
            west: -141
        };
        this.centreLatLng = {
            lat: 56,
            lng: -95
        };
        this.defaultZoom = 3;
        this.defaultZoomedIn = 12;

        // Setup province names.
        this.provinces = {
            AB: "Alberta",
            BC: "British Columbia",
            MB: "Manitoba",
            NB: "New Brunswick",
            NL: "Newfoundland and Labrador",
            NS: "Nova Scotia",
            NT: "Northwest Territories",
            NU: "Nunavut",
            ON: "Ontario",
            PEI: "Prince Edward Island",
            QC: "Quebec",
            SK: "Saskatchewan",
            YT: "Yukon"
        };

        // Setup store list.
        this.list = {
            groupByProv: true,
            section: this.querySelector('[data-location-list="section"]'),
            template: {
                heading: this.querySelector('[data-location-list="template-heading"]'),
                list: this.querySelector('[data-location-list="template-list"]'),
                item: this.querySelector('[data-location-list="template-item"]')
            }
        };

        // Setup store details.
        this.stores = [];
        this.details = {
            content: this.querySelector('[data-location-detail="content"]'),
            section: this.querySelector('[data-location-detail="section"]'),
            buttonCloseWrapper: this.querySelector('[data-location-detail="close-wrapper"]'),
            buttonClose: this.querySelector('[data-location-detail="close"]'),
            buttonDirections: this.querySelector('[data-location-detail="buttonDirections"]'),
            buttonPhone: this.querySelector('[data-location-detail="buttonPhone"]'),
            name: this.querySelector('[data-location-detail="name"]'),
            distance: this.querySelector('[data-location-detail="distance"]'),
            address: this.querySelector('[data-location-detail="address"]'),
            phone: this.querySelector('[data-location-detail="phone"]'),
            storeNotes: this.querySelector('[data-location-detail="storeNotes"]'),
            hours: this.querySelector('[data-location-detail="hours"]'),
            nearby: this.querySelector('[data-location-detail="nearby"]'),
            nearbyName: this.querySelector('[data-location-detail="nearby-name"]')
        };
        this.selectedStore = false;

        this.overlappingStores = [];
    }

    async connectedCallback() {
        try {
            if (!navigator.geolocation) this.buttonMyLocation.remove();

            this.details.buttonClose.setAttribute('href', this.basePath);
            this.details.buttonClose.addEventListener("click", event => {
                event.preventDefault();
                this.selectStore(false);
            });

            await this.initMap();
            await this.getStoreLocations();
            this.locationInPath();
        } catch (e) {
            this.showError(e);
        } finally {
            this.removeAttribute("data-loading");
            this.initial = false;
        }
    }

    showError(e) {
        this.innerHTML = `<p>Something went wrong while trying to get the store locations. Please refresh this page to try again.</p>`;
        console.error(e);
    }

    haversineCalculation(point1, point2, radius = 6367.5) {
        if (point1.lat) point1.latitude = point1.lat;
        if (point1.lng) point1.longitude = point1.lng;
        if (point2.lat) point2.latitude = point2.lat;
        if (point2.lng) point2.longitude = point2.lng;

        /**
         * @param {number} angle
         */
        function haversine(angle) {
            return 0.5 - Math.cos(angle) / 2;
        }

        /**
         * @param {number} degrees
         */
        function degreesToRadians(degrees) {
            return degrees * (Math.PI / 180);
        }

        return (2 * radius * Math.asin(Math.sqrt(haversine(degreesToRadians(point2.latitude - point1.latitude)) + Math.cos(degreesToRadians(point1.latitude)) * Math.cos(degreesToRadians(point2.latitude)) * haversine(degreesToRadians(point2.longitude - point1.longitude))))).toFixed(1);
    }

    async initMap() {
        // Get maps.
        const mapboxNav = new mapboxgl.NavigationControl();

        this.mapbox = new mapboxgl.Map({
            container: this.mapContainer,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [this.centreLatLng.lng, this.centreLatLng.lat],
            zoom: this.defaultZoom,
            maxBounds: [
                [this.latLngBounds.west, this.latLngBounds.south],
                [this.latLngBounds.east, this.latLngBounds.north]
            ],
        })
            .addControl(mapboxNav, 'top-left');

        // Setup current location button.
        this.buttonMyLocation.addEventListener("click", this.useMyLocation.bind(this));

        // Setup history tracking.
        window.addEventListener("popstate", this.locationInPath.bind(this));
        this.locationInPath();

        this.initSearch();
        this.initFilters();

        // Remove static map.
        if (this.mapStaticContainer) this.mapStaticContainer.remove();
    }

    async getStoreLocations() {
        try {
            // Get store locations.
            const ncolApiUrlLocations = `${ncol_api_url}/public/api/locations.json`;
            const fetchRes = await fetch(ncol_locations_json_url || ncolApiUrlLocations, { cache: 'no-cache' });
            const data = await fetchRes.json();
            this.stores = data.stores;
        } catch (e) {
            this.showError(e);
        }

        for (const store of this.stores) {
            // Extract and store brand and short name.
            if (store.name.includes("Quarks")) store.brand = "Quarks";
            else if (store.name.includes("Urban Trail")) store.brand = "Urban Trail";
            store.shortName = store.name.replace(/(Quarks|Urban Trail) - /, "");

            store.nearby.forEach(function (nearby) {
                if (nearby.name.includes("Quarks")) nearby.brand = "Quarks";
                else if (nearby.name.includes("Urban Trail")) nearby.brand = "Urban Trail";
            });

            store.listItem = this.initStoreListItem(store);
            store.marker = this.initStoreMarker(store);
        }

        // Show store list.
        this.showList();
    }

    initStoreListItem(store) {
        const listItem = this.list.template.item.content.cloneNode(true);
        const link = listItem.querySelector('[data-location-list="link"]');
        link.href = `${this.basePath}/${store.handle}`;
        listItem.querySelector('[data-location-list="brand"]').dataset.brand = store.brand;
        listItem.querySelector('[data-location-list="brand"]').textContent = store.brand;
        listItem.querySelector('[data-location-list="name"]').textContent = store.shortName;
        listItem.querySelector('[data-location-list="address"]').textContent = `${store.address1}, ${store.city}, ${store.province_code}`;

        const storeMap = this;
        link.addEventListener("click", (event) => {
            event.preventDefault();
            storeMap.selectStore(store);
        });

        return listItem.children[0];
    }

    initStoreMarker(store) {
        const coords = [store.longitude, store.latitude];

        // Get overlapping stores.
        const overlapping = store.nearby.filter(nearby => nearby.distance === 0);
        if (overlapping.length > 0) {
            // Check if this store is already recorded as overlapping.
            const alreadyRecorded = this.overlappingStores.find(group => group.includes(store.handle));
            if (alreadyRecorded) coords[0] -= 0.001;
            else {
                coords[0] += 0.001;
                this.overlappingStores.push([store.handle, ...overlapping.map(o => o.handle)]);
            }
        }

        const marker = new mapboxgl.Marker({
            color: store.brand === 'Urban Trail' ? '#4AA57C' : '#DD5347',
        })
            .setLngLat(coords)
            .addTo(this.mapbox);

        const markerElement = marker.getElement();
        markerElement.addEventListener("click", () => {
            this.selectStore(store);
        });
        return marker;
    }

    showList(fitMap = true) {
        this.list.section.innerHTML = "";

        if (!this.list.groupByProv) {
            const list = this.list.template.list.content.cloneNode(true).children[0];

            const heading = this.list.template.heading.content.cloneNode(true).children[0];
            heading.textContent = "Closest Stores";
            this.list.section.appendChild(heading);

            const storesByDistance = this.getStoresByDistance();
            for (const store of storesByDistance) {
                list.appendChild(store.listItem);
            }
            this.list.section.appendChild(list);

            if (fitMap)
                this.fitMapToMarkers(storesByDistance.map(store => [store.longitude, store.latitude]));
            return;
        }

        this.storesByDistance = null;

        const availableProvinces = {};
        const visibleStores = this.stores.filter(store => !store.hidden);
        for (const store of visibleStores) {
            if (!availableProvinces[store.province_code]) availableProvinces[store.province_code] = [];
            availableProvinces[store.province_code].push(store.listItem);
        }

        for (const code in this.provinces) {
            if (!availableProvinces[code]) continue;
            const province = availableProvinces[code];
            const list = this.list.template.list.content.cloneNode(true).children[0];
            const heading = this.list.template.heading.content.cloneNode(true).children[0];
            heading.textContent = this.provinces[code];
            this.list.section.appendChild(heading);
            province.forEach(function (store) {
                list.appendChild(store);
            });
            this.list.section.appendChild(list);
        }

        if (fitMap) this.fitMapToMarkers(visibleStores.map(store => [store.longitude, store.latitude]));
    }

    fitMapToMarkers(markers, padding = 50) {
        if (!markers)
            markers = this.stores
                .filter(store => !store.hidden)
                .map(store => [store.longitude, store.latitude]);

        const bounds = new mapboxgl.LngLatBounds();
        for (const marker of markers) {
            bounds.extend(marker);
        }

        if (bounds.isEmpty()) return;

        this.mapbox.fitBounds(bounds, {
            padding,
            maxZoom: this.defaultZoomedIn
        });
    }

    async locationInPath() {
        const path = window.location.pathname;
        const storeHandle = path.replace(/\/$/, "").split("/").pop();
        if (!storeHandle) return this.selectStore(false);

        // If handle ends with "quarks" or "urban-trail", select the type instead.
        if (storeHandle === "quarks" || storeHandle === "urban-trail") {
            this.filters[storeHandle === "quarks" ? "quarks" : "urbanTrail"].checked = true;
            this.filterStores();
            await this.selectStore(false);
            return;
        }

        const store = this.stores.find((store) => store.handle === storeHandle);
        await this.selectStore(store || false);
    }

    setSearching(isSearching, type) {
        this.searching = isSearching;
        this.searchInput.disabled = isSearching;
        this.buttonSearch.disabled = isSearching;
        this.buttonMyLocation.disabled = isSearching;
        this.feedback.removeAttribute("data-status");
        this.feedback.textContent = "";
        if (type === "myLocation") this.buttonMyLocation.dataset.loading = isSearching;
        else this.buttonSearch.dataset.loading = isSearching;
    }

    resetMap() {
        this.mapbox.flyTo({
            center: [this.centreLatLng.lng, this.centreLatLng.lat],
            zoom: this.defaultZoom
        });
        this.list.groupByProv = true;
        this.stores.forEach(function (store) {
            store.distanceToLocation = null;
            store.listItem.querySelector('[data-location-list="distance"]').textContent = "";
        });
        this.showList();
    }

    setMapPosition(position, calculateDistance = true) {
        if (!position) {
            if (!this.mapPosition.lat || !this.mapPosition.lng) return;
            position = this.mapPosition;
        }

        const parsedPos = {
            lat: typeof position.lat === "function" ? position.lat() : position.latitude || position.lat,
            lng: typeof position.lng === "function" ? position.lng() : position.longitude || position.lng
        };

        if (calculateDistance) {
            this.mapPosition = parsedPos;

            // Update store location distances.
            this.stores.forEach(function (store) {
                const distanceToLocation = this.haversineCalculation(parsedPos, store);
                store.distanceToLocation = distanceToLocation;
                store.listItem.querySelector('[data-location-list="distance"]').textContent = `${store.distanceToLocation} km away`;
            }, this);
        }

        const target = {
            center: [parsedPos.lng, parsedPos.lat],
            zoom: this.mapbox.getZoom() < this.defaultZoomedIn
                ? this.defaultZoomedIn
                : this.mapbox.getZoom()
        };

        this.mapbox.once('moveend', () => {
            if (this.selectedStore?.marker) this.selectedStore.marker.addClassName('selected-marker-animate');
        });

        if (this.initial) this.mapbox.jumpTo(target);
        else this.mapbox.flyTo(target);

        if (calculateDistance) {
            const storesByDistance = this.getStoresByDistance();
            this.fitMapToMarkers([
                [parsedPos.lng, parsedPos.lat],
                [storesByDistance[0].longitude, storesByDistance[0].latitude]
            ], 100)
        }
    }

    getStoresByDistance() {
        return this.stores
            .filter(store => !store.hidden)
            .toSorted(function (a, b) {
                return a.distanceToLocation - b.distanceToLocation;
            });
    }

    initSearch() {
        const onSubmit = async (event) => {
            event.preventDefault();
            this.selectStore(false);
            if (this.searching) return;

            // If the search value hasn't changed, don't run geocoder.
            const value = this.searchInput.value;
            if (value === "") return this.resetMap();
            if (value === this.prevSearch) return this.setMapPosition();

            this.setSearching(true);
            this.prevSearch = value;

            try {
                const urlQuery = new URLSearchParams({
                    q: value,
                    access_token: mapboxgl.accessToken,
                    limit: 1,
                    country: "ca",
                    bbox: `${this.latLngBounds.west},${this.latLngBounds.south},${this.latLngBounds.east},${this.latLngBounds.north}`
                });
                const response = await fetch(`https://api.mapbox.com/search/geocode/v6/forward?${urlQuery.toString()}`);
                const data = await response.json();
                if (!data.features || data.features.length === 0) throw new Error("No results from Mapbox Geocoding API");

                const coordinates = data.features[0].geometry?.coordinates;
                if (!coordinates || coordinates.length < 2) throw new Error("Invalid coordinates from Mapbox Geocoding API");

                const location = {
                    lat: coordinates[1],
                    lng: coordinates[0]
                };
                this.setMapPosition(location);
                this.list.groupByProv = false;
                this.showList(false);
                this.setSearching(false);
            } catch (error) {
                this.setSearching(false);
                console.error(`[Search] ${error}`);
                this.feedback.dataset.status = "error";
                this.feedback.textContent = "No locations found. Please enter a city, province or postal code, or look for a location in the list.";
                this.resetMap();
            }
        }

        this.formSearch.addEventListener("submit", onSubmit);

        if (!this.selectedStore && this.formSearch.hasAttribute('hidden'))
            this.formSearch.removeAttribute('hidden');
    }

    useMyLocation() {
        if (this.searching) return;

        if (!navigator.geolocation) {
            this.feedback.dataset.status = "error";
            this.feedback.textContent = "Your browser does not support geolocation.";
            return this.buttonMyLocation.remove();
        }
        this.selectStore(false);
        this.setSearching(true, "myLocation");
        
        const result = (position, save = true) => {
            if (save) this.geolocationPosition = position.coords;
            this.setMapPosition(position.coords);
            this.prevSearch = false;
            this.setSearching(false, "myLocation");
            this.list.groupByProv = false;
            this.showList(false);
        };

        // If we already have a position, use it.
        if (this.geolocationPosition) {
            return result(this.geolocationPosition, false);
        }

        navigator.geolocation.getCurrentPosition(
            result,
            (error) => {
                this.setSearching(false, "myLocation");
                this.buttonMyLocation.remove();
                this.feedback.dataset.status = "error";
                if (error.code === 1) this.feedback.textContent = "You have denied access to your location. Please enter your location manually.";
                else this.feedback.textContent = "Unable to get your location. Please enter your location manually.";
                console.error(`[navigator.geolocation] ${error}`);
            }
        );
    }

    async selectStore(store = false) {
        if (!this.mapbox) await this.initMap();

        if (store === this.selectedStore) return;

        // Reset previous selected store.
        if (this.selectedStore) {
            this.selectedStore.marker.removeClassName("selected-marker");
            this.selectedStore.marker.removeClassName("selected-marker-animate");
        }

        // Set new current store.
        if (typeof store === "string") store = this.stores.find((s) => s.handle === store);

        this.selectedStore = store;
        let storePath = store ? `/${encodeURI(store.handle)}` : "";
        history.pushState({}, "", this.basePath + storePath);

        if (store) document.title = `${store.name} | ${this.documentTitle}`;
        else document.title = this.documentTitle;

        this.setStructuredData(store);

        if (!store) return this.toggleDetailsPanel(false);
        else this.toggleDetailsPanel(true, store);
    }

    setStructuredData(store) {
        // Remove any existing structured data.
        const existingScript = document.querySelector('script[type="application/ld+json"]');
        if (existingScript) existingScript.remove();

        if (!store) return;

        // Build structured data.
        const structuredData = {
            "@context": "https://schema.org",
            "@type": "Store",
            "address": {
                "@type": "PostalAddress",
                "streetAddress": store.address1 || "",
                "extendedAddress": store.address2 || "",
                "addressLocality": store.city || "",
                "addressRegion": store.province_code || "",
                "postalCode": store.zip || "",
                "addressCountry": store.country_code || "CA"
            },
            "name": store.name || "",
            "geo": {
                "@type": "GeoCoordinates",
                "latitude": store.latitude || "",
                "longitude": store.longitude || ""
            },
            "openingHoursSpecification": [],
            "telephone": store.phone || "",
            "url": window.location.href,
            "currenciesAccepted": "CAD",
            "paymentAccepted": "Cash, Credit Card"
        };

        // Add store hours.
        const convertHoursToTimeSchema = (time) => {
            if (!time) return null;
            const match = time.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)/i);
            if (!match) return null;

            let hours = parseInt(match[1]);
            const minutes = match[2] ? parseInt(match[2]) : 0;
            const period = match[3].toUpperCase();

            if (period === "PM" && hours < 12) hours += 12;
            if (period === "AM" && hours === 12) hours = 0;

            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
        }

        const validSchemaDayOfWeek = [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday"
        ];

        for (const item of store.store_hours || []) {
            // Convert "Monday~9:00 AM–5:00 PM" to structured data.
            const [day, hours] = item.split("~");
            if (!day || !hours) continue;

            if (!validSchemaDayOfWeek.includes(day)) continue;

            let [openTime, closeTime] = hours.split("–").map(t => t.trim());
            if (!openTime || !closeTime) continue;

            openTime = convertHoursToTimeSchema(openTime);
            closeTime = convertHoursToTimeSchema(closeTime);
            if (!openTime || !closeTime) continue;

            const openingHoursSpec = {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": `https://schema.org/${day}`,
                "closes": closeTime,
                "opens": openTime
            };

            structuredData.openingHoursSpecification.push(openingHoursSpec);
        }

        // Insert structured data.
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(structuredData, null, 2);
        document.head.appendChild(script);
    }

    async toggleDetailsPanel(open, store = false) {
        const initial = this.initial;
        const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

        const doToggleDetailsPanel = () => {
            if (open) {
                this.formSearch.setAttribute('hidden', 'hidden');
                this.details.buttonCloseWrapper.removeAttribute('hidden');
            } else {
                this.formSearch.removeAttribute('hidden');
                this.details.buttonCloseWrapper.setAttribute('hidden', 'hidden');
            }

            if (open && store !== false) this.showStoreDetails(store);
            this.list.section.style.display = open ? "none" : null;
            this.details.section.style.display = open ? null : "none";

            if (!initial && open) this.details.content.scrollIntoView({ behavior: 'smooth' });
        }

        if (initial || !document.startViewTransition || windowWidth < 768) {
            doToggleDetailsPanel();
            return;
        }

        const transition = document.startViewTransition(async () => {
            doToggleDetailsPanel();
        });

        try {
            await transition.finished;
        } catch (e) {
            doToggleDetailsPanel();
        }
    }

    showStoreDetails(store) {
        // Set map position.
        if (this.mapbox) {
            if (store.marker) store.marker.addClassName('selected-marker');
            this.setMapPosition(store, false);
        }

        // Insert store name.
        if (store.name) {
            this.details.name.textContent = store.name;
            this.details.nearbyName.textContent = store.name;
        } else {
            this.details.name.textContent = "Coming Soon";
            this.details.nearbyName.textContent = "";
        }

        // Insert store distance.
        if (store.distanceToLocation) this.details.distance.textContent = `${store.distanceToLocation} km away`;
        else this.details.distance.textContent = "";

        // Insert store address.
        if (store.address1) {
            this.details.buttonDirections.setAttribute("href", "https://www.google.com/maps/dir/?api=1&destination=" + encodeURIComponent(`${store.address1}, ${store.city}, ${store.province_code}, ${store.zip}`).replace(/%20/g, "+"));
            this.details.address.innerHTML = `${store.address1}<br/>${store.address2 ? `${store.address2}<br/>` : ""}${store.city}, ${store.province_code} ${store.zip}`;
        } else {
            this.details.buttonDirections.setAttribute("href", "#");
            this.details.address.innerHTML = "";
        }

        // Insert store phone.
        if (store.phone) {
            this.details.buttonPhone.setAttribute("href", "tel:" + encodeURIComponent(store.phone));
            this.details.phone.textContent = store.phone.replace(/(?:\+1)*(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
        } else {
            this.details.buttonPhone.setAttribute("href", "#");
            this.details.phone.textContent = "";
        }

        // Insert store notes.
        this.details.storeNotes.innerHTML = "";
        if (store.display_note) this.details.storeNotes.textContent = store.display_note;

        // Insert store hours.
        this.details.hours.innerHTML = "";
        if (store.store_hours && store.store_hours.length) {
            var storeHoursTemplate = this.querySelector("[data-location-hours='template']");

            store.store_hours.forEach(function (item) {
                var storeHour = item.split("~");
                var storeHourElement = storeHoursTemplate.content.cloneNode(true);
                storeHourElement.querySelector("[data-location-hours='day']").textContent = storeHour[0];
                storeHourElement.querySelector("[data-location-hours='times']").textContent = storeHour[1].replace(/\–/, " – ");
                this.details.hours.appendChild(storeHourElement);
            }, this);
        } else this.details.hours.innerHTML = "Call store for current store hours.";

        // Insert nearby stores.
        this.details.nearby.innerHTML = "";
        if (store.nearby) {
            var storeNearbyTemplate = document.querySelector("[data-location-nearby='template']");
            let count = 0;
            for (const item of store.nearby) {
                if (count > 2 || item.distance > 200) break;
                if (item.hidden) continue;
                count++;

                var storeNearbyElement = storeNearbyTemplate.content.cloneNode(true);

                var link = storeNearbyElement.querySelector("[data-location-nearby='link']");
                link.href = `${this.basePath}/${item.handle}`;
                link.textContent = item.name;
                link.addEventListener("click", (event) => {
                    event.preventDefault();
                    this.selectStore(item.handle);
                });

                storeNearbyElement.querySelector("[data-location-nearby='distance']").textContent = `${item.distance.toFixed(1)} km`;
                this.details.nearby.appendChild(storeNearbyElement);
            }
            if (count === 0) this.details.nearby.textContent = "Use the map to see other nearby stores.";
        } else {
            this.details.nearby.textContent = "Use the map to see other nearby stores.";
        }
    }

    initFilters() {
        Object.entries(this.filters).forEach(([key, filter]) => {
            filter.addEventListener("change", this.filterStores.bind(this));
        }, this);
    }

    filterStores() {
        this.selectStore();
        const filters = [];
        if (this.filters.quarks.checked) filters.push("Quarks");
        if (this.filters.urbanTrail.checked) filters.push("Urban Trail");

        this.stores.forEach((store) => {
            if (!filters.length || filters.includes(store.brand)) {
                store.marker.addTo(this.mapbox);
                store.hidden = false;
            } else {
                store.marker.remove();
                store.hidden = true;
            }

            store.nearby.forEach((nearby) => {
                if (!filters.length || filters.includes(nearby.brand)) {
                    nearby.hidden = false;
                } else {
                    nearby.hidden = true;
                }
            });
        });

        this.showList();
    }
}
customElements.define("store-locations", StoreLocations);
